/* Where the map should open, derived from the browser's timezone.

   No DOM, no Lit, no network, no permission prompt — the same layer as
   `text.ts`. The whole feature is two pure functions plus a generated table.

   Why timezone and not the IP: an IP lookup means either a third-party call in
   the request path or a 60MB GeoLite2 database in the image, and both buy
   city-level precision this screen does not use. `Intl` already knows the zone,
   answers synchronously before the Maps API has even loaded, and asks the user
   nothing.

   Country is the ONLY thing derived from it. A country centroid is a poor
   camera target — the middle of India is nowhere anyone saved — so the country
   is used to *select venues from the user's own library*, and the map frames
   those. See ADR 0005. */

import { COUNTRY_NAMES, TZ_COUNTRY } from './geo-home.data';

/**
 * Deprecated zone ids that browsers still report, mapped to their canonical
 * name. zone.tab carries only canonical zones, so without these the users on
 * them fall through to the fit-all fallback for no reason.
 *
 * Deliberately short. ECMA-402 says `Intl` returns canonical ids, and modern
 * engines do; this covers the ones that show up anyway — older Android WebViews
 * and Safari — rather than mirroring the whole `backward` file. Add to it when a
 * real device reports something missing, not speculatively.
 */
const BACKWARD: Readonly<Record<string, string>> = {
  'Africa/Asmera': 'Africa/Asmara',
  'America/Buenos_Aires': 'America/Argentina/Buenos_Aires',
  'America/Godthab': 'America/Nuuk',
  'America/Indianapolis': 'America/Indiana/Indianapolis',
  'America/Montreal': 'America/Toronto',
  'Asia/Calcutta': 'Asia/Kolkata',
  'Asia/Chongqing': 'Asia/Shanghai',
  'Asia/Dacca': 'Asia/Dhaka',
  'Asia/Harbin': 'Asia/Shanghai',
  'Asia/Istanbul': 'Europe/Istanbul',
  'Asia/Katmandu': 'Asia/Kathmandu',
  'Asia/Rangoon': 'Asia/Yangon',
  'Asia/Saigon': 'Asia/Ho_Chi_Minh',
  'Asia/Thimbu': 'Asia/Thimphu',
  'Asia/Ulan_Bator': 'Asia/Ulaanbaatar',
  'Atlantic/Faeroe': 'Atlantic/Faroe',
  'Australia/Canberra': 'Australia/Sydney',
  'Europe/Belfast': 'Europe/London',
  'Europe/Kiev': 'Europe/Kyiv',
  'Europe/Nicosia': 'Asia/Nicosia',
  'Europe/Uzhgorod': 'Europe/Kyiv',
  'Europe/Zaporozhye': 'Europe/Kyiv',
  'Pacific/Ponape': 'Pacific/Pohnpei',
  'Pacific/Truk': 'Pacific/Chuuk',
};

/**
 * Country names the generated table cannot supply, keyed by the same normalized
 * form. Two kinds, and both are load-bearing:
 *
 * - What people actually write. `country` is free text from Gemini, and the live
 *   library holds "USA", "England" and "UAE" — none of which is an ISO label.
 * - What the generator dropped as ambiguous. "uk" is claimed by both Britain and
 *   the British Virgin Islands, so it is omitted there and resolved here.
 *
 * These win over the generated entries. Not included: sub-national names like
 * "Bali" (one row in the live library) — a region-to-country table has no
 * natural end, and the cost of a miss is one venue not counted toward the
 * opening frame.
 */
const ALIASES: Readonly<Record<string, string>> = {
  america: 'US',
  britain: 'GB',
  burma: 'MM',
  'cape verde': 'CV',
  czechia: 'CZ',
  'east timor': 'TL',
  england: 'GB',
  'great britain': 'GB',
  holland: 'NL',
  'ivory coast': 'CI',
  macedonia: 'MK',
  'north korea': 'KP',
  'northern ireland': 'GB',
  scotland: 'GB',
  'south korea': 'KR',
  swaziland: 'SZ',
  turkiye: 'TR',
  uae: 'AE',
  uk: 'GB',
  'united kingdom': 'GB',
  'united states of america': 'US',
  us: 'US',
  usa: 'US',
  wales: 'GB',
};

const NAME_TO_CC: Readonly<Record<string, string>> = { ...COUNTRY_NAMES, ...ALIASES };

/** Fold a country name to its match key. Mirrors normalize() in the generator —
    keep the two in step or a generated key becomes unreachable. */
function normalize(s: string): string {
  return s
    .normalize('NFD')
    // \u0300-\u036f written as escapes, not literals: the range ends are invisible
    // combining marks, and an editor that normalised one would break the match
    // with nothing to see in the diff (same reasoning as text.ts).
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/^the /, '');
}

/**
 * The user's country as ISO 3166-1 alpha-2, or null when it can't be known.
 *
 * Null is a normal answer, not an error: Tor and Firefox's resistFingerprinting
 * force UTC, and a zone can be newer than the committed table. Callers fall back
 * rather than guess.
 */
export function localCountryCode(): string | null {
  let zone: string | undefined;
  try {
    zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return null;
  }
  if (!zone) return null;
  // UTC/GMT/Etc-* is either a spoof or a machine with no zone set. Both mean
  // "no country", and Etc/GMT+5 in particular is an offset, not a place.
  if (zone === 'UTC' || zone === 'GMT' || zone.startsWith('Etc/')) return null;
  return TZ_COUNTRY[zone] ?? TZ_COUNTRY[BACKWARD[zone] ?? ''] ?? null;
}

/** True when a place's free-text country resolves to `code`. */
export function countryMatches(code: string, name: string | null | undefined): boolean {
  if (!name) return false;
  return NAME_TO_CC[normalize(name)] === code;
}
